import { useMemo } from 'react';

import { useMutationMiddleware } from '@/features/v2/hooks/useMutationMiddleware';

import { useFormDataQuery } from '@/api/getters';
import { DocumentStatuses, FormData, Stages } from '@/api/types';
import ChatMessage from '@/components/ChatMessage';
import { isVehicleDamageProduct } from '@/components/helpers/productHelpers';
import { Form } from '@/components/ui/Form';
import ProgressBar, { Step, StepBase } from '@/components/ui/ProgressBar';
import { PRODUCT } from '@/consts';
import { PRODUCT_MAP } from '@/features/v2/forms';
import { StepperProvider, useStepper } from '@/features/v2/lib/stepper';
import { PRODUCT_DATA } from '@/features/v2/product-data';
import ProgressFinished from '@/features/v2/vehicle-damage/components/thankYou/ProgressFinished';
import cn from '@/utils/cn';

import { AdditionalInfo, useAdditionalInfoContext } from './additionalInfo';
import constate from 'constate';
import { useForm, useFormContext } from 'react-hook-form';

export type DeadEnds = {
  [key: string]: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    condition: (value: Record<any, any>, currentInnerStep: number) => boolean;
    returnToStep: number;
  };
};

export interface ExtendedSteps extends Omit<Step, 'subSteps'> {
  subSteps?: (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: Record<any, any>,
    additionalInfo?: AdditionalInfo
  ) => StepBase[];
  deadEnds?: ((additionalInfo: AdditionalInfo) => DeadEnds) | DeadEnds;
  component?: () => React.ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  backButton?: (innerStep?: number, value?: Record<any, any>) => boolean;
  lastStep: (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: Record<any, any>,
    additionalInfo?: AdditionalInfo
  ) => {
    lastStep: number;
    additionalStep: boolean;
    secondAdditionalStep?: string;
    dryingReport?: boolean;
  };
  visibleInProgressBar?: (step: number, isComplementary: boolean) => boolean;
  progressPoints?: (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: Record<any, any>,
    additionalInfo?: AdditionalInfo
  ) => Array<string>;
  completeValidation?: (innerStep?: number) => boolean;
  chatMessage?: (
    userName: string,
    innerStep?: number,
    amount?: number
  ) => string | React.ReactNode;
  hideChatMessageCloseButton?: boolean;
}

export type ProgressType =
  | 'finished'
  | 'default'
  | 'not_qualified'
  | 'contact_us'
  | 'finished_stage_I'
  | 'offer'
  | 'conflict'
  | 'ready_doc'
  | 'completed'
  | 'ups';

export const [WizardInnerProvider, useWizard] = constate(
  ({ allSteps }: { allSteps: ExtendedSteps[] | null }) => {
    return {
      allSteps,
    };
  }
);

export const WizardProvider = ({
  data,
  children,
}: {
  data?: FormData;
  children: React.ReactNode;
}) => {
  const isSummaryFormVehicle =
    isVehicleDamageProduct(data?.product.id) &&
    (data?.stage === Stages.STAGE_IV || data?.stage === Stages.STAGE_III);

  const isHHWithOnBlurMode =
    data?.product?.id === PRODUCT.HOUSE_HOLD &&
    data?.stage === Stages.STAGE_III;

  const isTransmission = data?.product?.id === PRODUCT.TRANSMISSION;

  const withOnBlurMode =
    isSummaryFormVehicle || isHHWithOnBlurMode || isTransmission;

  const values = {
    ...data?.complementary_default_values,
    ...data?.payload?.data,
  };

  const methodsDefault = useForm({
    values,
    reValidateMode: 'onChange',
    mode: 'onSubmit',
    shouldUnregister: true,
  });

  const methodsWithOnBlur = useForm({
    values,
    reValidateMode: 'onBlur',
    mode: 'onBlur',
    shouldUnregister: false,
  });

  const methods = withOnBlurMode ? methodsWithOnBlur : methodsDefault;

  const steps = useMemo(() => {
    if (!data) return null;
    const productId = data.product.id;
    if (productId in PRODUCT_MAP) return PRODUCT_MAP[productId];
    return null;
  }, [data]);

  if (!data || !steps) return null;

  return (
    <WizardInnerProvider allSteps={steps}>
      <Form {...methods}>
        <StepperProvider
          defaultStep={data.payload?.stepper?.step}
          innerObject={data.payload?.stepper?.innerStep}
          defaultstepTag={data.step}
        >
          {children}
        </StepperProvider>
      </Form>
    </WizardInnerProvider>
  );
};

const ConditionalRender = ({
  children,
  visibleCSS,
}: {
  children: React.ReactNode;
  visibleCSS: boolean;
}) => {
  return <div className={visibleCSS ? 'block' : 'hidden'}>{children}</div>;
};

export const FormComponent = () => {
  const { step, innerStep } = useStepper();
  const { allSteps } = useWizard();

  const { data } = useFormDataQuery();

  useMutationMiddleware();

  if (!data) return null;

  const isTransmission = data.product.id === PRODUCT.TRANSMISSION;

  const currentStep = PRODUCT_DATA[data.product.id]?.schema[step];
  const chatMessage = currentStep?.chatMessage;

  const label =
    chatMessage && typeof chatMessage === 'function'
      ? chatMessage(data.user.name, innerStep, data?.offer.surcharge_summary)
      : '';

  return (
    <>
      <ChatMessage
        description={label}
        variant={'primary'}
        className={isTransmission ? 'pb-0' : undefined}
        consultant={PRODUCT_DATA[data.product.id].guardian}
        hideCloseButton={
          isTransmission || !!currentStep?.hideChatMessageCloseButton
        }
      />
      {allSteps?.map((stepObj, idx) => {
        if (stepObj.component) {
          return (
            <ConditionalRender
              key={`${stepObj.label}`}
              visibleCSS={step === idx}
            >
              {stepObj.component()}
            </ConditionalRender>
          );
        }
      })}
    </>
  );
};

export const Stepper = ({ progressType }: { progressType: ProgressType }) => {
  const { allSteps } = useWizard();
  const { watch } = useFormContext();
  const { data } = useFormDataQuery();
  const { additionalInfo } = useAdditionalInfoContext();
  const { step, innerStep, progress } = useStepper();
  if (!data) return null;
  if (!allSteps) return null;

  const mappedSteps = allSteps.map((step) => {
    const isSubStep = !!step.subSteps;
    const executedSubSteps = isSubStep
      ? step.subSteps!(watch(), additionalInfo)
      : undefined;
    return {
      ...step,
      subSteps: executedSubSteps,
    };
  });

  const subSteps = mappedSteps[step]?.subSteps;

  const newStepsWithSummarySteps = mappedSteps.filter((item) => {
    return item.visibleInProgressBar
      ? item.visibleInProgressBar(step, !!data.offer.is_complementary)
      : true;
  });

  const isSubStep = !!subSteps;

  // logic of generated documents progress
  const generatedDocumentsProgress = () => {
    if (data.stage === Stages.STAGE_V && data.documents.length > 0) {
      return (
        (data?.documents?.filter(
          (doc) => doc.status === DocumentStatuses.COMPLETED
        ).length /
          data?.documents?.length) *
        100
      );
    }
    if (data.stage === Stages.STAGE_V_FINISHED) {
      return 100;
    }
    return null;
  };

  const currentStepHelper = (step: number) => {
    // 4 is the number of steps before the summary steps
    const isSummaryStep = step >= 4;

    if (isSummaryStep) return data.offer.is_complementary ? step - 4 : step - 5;
    return step;
  };

  const isFinished = step === 8;

  return (
    <div
      className={cn(
        'fixed inset-x-1/2 top-[75px] z-40 w-full -translate-x-1/2 lg:sticky lg:top-4 lg:w-fit lg:-translate-x-0',
        'lg:max-w-[290px]'
      )}
    >
      <div className={cn('relative w-full')}>
        <span className="translate-z-0 absolute left-0 top-[-12px] flex h-[41px] w-screen bg-oxford-blue-400 lg:hidden" />
        <div className="mx-auto px-2.5 sm:max-w-screen-sm sm:px-4 md:max-w-tablet md:px-6 lg:max-w-none lg:px-0">
          {progressType === 'default' && (
            <ProgressBar
              variant={isSubStep ? 'sub-step' : 'default'}
              progress={generatedDocumentsProgress() ?? progress ?? 0}
              currentStep={currentStepHelper(step + 1)}
              steps={newStepsWithSummarySteps}
              currentSubStep={innerStep + 1}
              isFinished={isFinished}
              subSteps={subSteps}
            />
          )}
          {progressType !== 'default' && (
            <ProgressFinished
              steps={[
                ...newStepsWithSummarySteps.map((item, idx) => {
                  return {
                    label: item.label,
                    active: idx <= step,
                    checked: idx < step || step === 3,
                  };
                }),
              ]}
              variant={progressType}
            />
          )}
        </div>
      </div>
    </div>
  );
};
